// Marketing → Settings: when posts go out (Hawaii windows), the pillar mix
// the daily brain aims for, how far ahead it fills the queue, reply limits,
// and the Threads account with an on-demand health check.
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ErrorNote, Field, Note } from '../../components/ui';
import { errorText } from '../../lib/backbarApi';
import { ago } from '../../lib/format';
import { hstDateTime, mk, PILLARS, pillarLabel, type MkSettings, type Pillar, type ThreadsHealth } from '../../lib/marketing';
import { MkPage } from './shared';

type Flash = { tone: 'good' | 'warn'; text: string } | null;
const DEFAULT_WINDOWS: [string, string][] = [['07:00', '09:00'], ['12:00', '14:00'], ['17:30', '20:00']];
const round2 = (n: number) => Math.round(n * 100) / 100;

export function MarketingSettings() {
  const [s, setS] = useState<MkSettings | null>(null);
  const [tokenIssued, setTokenIssued] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);

  const [perDay, setPerDay] = useState(3);
  const [windows, setWindows] = useState<[string, string][]>(DEFAULT_WINDOWS);
  const [weights, setWeights] = useState<Record<Pillar, number>>(() => evenWeights());
  const [horizon, setHorizon] = useState(14);
  const [capDay, setCapDay] = useState(6);
  const [capAuthor, setCapAuthor] = useState(1);
  const [health, setHealth] = useState<ThreadsHealth>({ status: 'unconfigured' });
  const [healthAt, setHealthAt] = useState<string | null>(null);

  useEffect(() => {
    mk.settings().then((r) => {
      const v = r.settings;
      setS(v);
      setTokenIssued(r.token_issued_at);
      const t = v.posting_cadence?.threads;
      if (t) { setPerDay(Number(t.per_day) || 0); if (Array.isArray(t.windows_hst) && t.windows_hst.length) setWindows(t.windows_hst.map((w) => [w[0], w[1]] as [string, string])); }
      if (v.pillar_weights) {
        const w = evenWeights();
        for (const p of PILLARS) if (v.pillar_weights[p] != null) w[p] = Number(v.pillar_weights[p]);
        setWeights(w);
      }
      if (v.queue_horizon_days != null) setHorizon(Number(v.queue_horizon_days));
      if (v.reply_caps) { setCapDay(Number(v.reply_caps.per_day)); setCapAuthor(Number(v.reply_caps.per_author_per_day)); }
      if (v.threads_health) setHealth(v.threads_health);
      setHealthAt(r.updated_at?.threads_health ?? null);
    }).catch(setErr);
  }, []);

  const sum = round2(PILLARS.reduce((a, p) => a + (weights[p] || 0), 0));
  const sumOk = Math.abs(sum - 1) <= 0.01;

  return (
    <MkPage title="Settings" blurb="When posts go out, what the daily brain writes about, and the Threads connection. Times are Hawaii time.">
      <ErrorNote error={err} notConfigured="Marketing is not set up on the server yet." />
      {!s && !err && <p className="lp-empty">Loading…</p>}
      {s && (
        <div className="bk-grid">
          <Card span={6} title="Posting schedule" sub="Hawaii time">
            {(save) => (
              <>
                <Field label="Posts per day" hint="The poster never puts two posts within 2 hours of each other.">
                  <input type="number" className="lp-inp" min={0} max={6} value={perDay} onChange={(e) => setPerDay(Number(e.target.value))} style={{ maxWidth: 120 }} />
                </Field>
                <div className="lp-field-label" style={{ marginBottom: 6 }}>Posting windows</div>
                {windows.map((w, i) => (
                  <div key={i} className="mk-window">
                    <input type="time" className="lp-inp" aria-label={`Window ${i + 1} starts`} value={w[0]} onChange={(e) => setWindows((ws) => ws.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />
                    <span>to</span>
                    <input type="time" className="lp-inp" aria-label={`Window ${i + 1} ends`} value={w[1]} onChange={(e) => setWindows((ws) => ws.map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
                    {windows.length > 1 && <button type="button" className="lp-btn lp-btn--small lp-btn--quiet" onClick={() => setWindows((ws) => ws.filter((_, j) => j !== i))}>Remove</button>}
                  </div>
                ))}
                {windows.length < 6 && <button type="button" className="lp-btn lp-btn--small" onClick={() => setWindows((ws) => [...ws, ['10:00', '11:00']])}>Add a window</button>}
                <div className="lp-actions">
                  <button type="button" className="lp-btn lp-btn--primary" onClick={() => save('Posting schedule saved.', () => mk.saveSetting('posting_cadence', { ...(s.posting_cadence ?? {}), threads: { per_day: perDay, windows_hst: windows } }))}>Save schedule</button>
                </div>
              </>
            )}
          </Card>

          <Card span={6} title="Threads account" sub="Read only">
            {(save) => (
              <>
                <dl className="lp-kv">
                  <dt>Status</dt><dd><span className={`mk-dot mk-dot--${health.status ?? 'unconfigured'}`} style={{ display: 'inline-block', marginRight: 6 }} />{statusWords(health.status)}</dd>
                  <dt>Account</dt><dd>{health.username ? `@${String(health.username)}` : '—'}</dd>
                  <dt>Followers</dt><dd>{health.followers != null ? Number(health.followers).toLocaleString('en-US') : '—'}</dd>
                  <dt>Token</dt><dd>{tokenIssued ? `Refreshed ${hstDateTime(tokenIssued)}` : 'From the one-time setup'}</dd>
                  <dt>Last check</dt><dd>{healthAt ? `${hstDateTime(healthAt)} (${ago(healthAt)})` : '—'}</dd>
                </dl>
                {(health.error || health.message) && health.status !== 'ok' && <Note tone="warn">{String(health.error ?? health.message)}</Note>}
                {health.status === 'unconfigured' && <p className="bk-blurb" style={{ marginTop: 10 }}>Not connected yet. <Link className="bk-link" to="/help#set-up-threads">Set up Threads →</Link></p>}
                <div className="lp-actions">
                  <button type="button" className="lp-btn" onClick={() => save(null, async () => {
                    const r = await mk.healthNow();
                    setHealth(r.health ?? { status: 'unconfigured' });
                    setHealthAt(r.health_updated_at);
                    return r.ok ? 'Health check done: connected.' : `Health check done: ${r.reason === 'unconfigured' ? 'unconfigured (Threads is not connected yet)' : r.reason}.`;
                  })}>Run health check now</button>
                </div>
              </>
            )}
          </Card>

          <Card span={6} title="Pillar mix" sub="What the daily brain aims for">
            {(save) => (
              <>
                {PILLARS.map((p) => (
                  <div key={p} className="mk-weight">
                    <label htmlFor={`w-${p}`}>{pillarLabel(p)}</label>
                    <input id={`w-${p}`} type="range" min={0} max={1} step={0.01} value={weights[p]} onChange={(e) => setWeights((w) => ({ ...w, [p]: Number(e.target.value) }))} />
                    <span className="mk-weight-val">{Math.round(weights[p] * 100)}%</span>
                  </div>
                ))}
                <div className={`mk-sum${sumOk ? '' : ' mk-sum--bad'}`}>Total {Math.round(sum * 100)}%{sumOk ? '' : ' · must be 100%'}</div>
                <div className="lp-actions">
                  <button type="button" className="lp-btn" disabled={sum === 0} onClick={() => setWeights((w) => scaleToOne(w))}>Scale to 100%</button>
                  <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setWeights(evenWeights())}>Even split</button>
                  <button type="button" className="lp-btn lp-btn--primary" disabled={!sumOk} onClick={() => save('Pillar mix saved.', () => mk.saveSetting('pillar_weights', normalize(weights)))}>Save mix</button>
                </div>
              </>
            )}
          </Card>

          <Card span={6} title="Queue and replies">
            {(save) => (
              <>
                <Field label="Fill the queue this many days ahead" hint="The daily brain tops the queue up to this horizon.">
                  <input type="number" className="lp-inp" min={1} max={90} value={horizon} onChange={(e) => setHorizon(Number(e.target.value))} style={{ maxWidth: 120 }} />
                </Field>
                <div className="lp-actions" style={{ marginTop: 0, marginBottom: 14 }}>
                  <button type="button" className="lp-btn lp-btn--primary" onClick={() => save('Queue horizon saved.', () => mk.saveSetting('queue_horizon_days', horizon))}>Save horizon</button>
                </div>
                <div className="mk-edit-grid" style={{ marginTop: 0 }}>
                  <Field label="Replies per day"><input type="number" className="lp-inp" min={0} max={50} value={capDay} onChange={(e) => setCapDay(Number(e.target.value))} /></Field>
                  <Field label="Replies to one person per day"><input type="number" className="lp-inp" min={0} max={50} value={capAuthor} onChange={(e) => setCapAuthor(Number(e.target.value))} /></Field>
                </div>
                <div className="lp-actions" style={{ marginTop: 0 }}>
                  <button type="button" className="lp-btn lp-btn--primary" onClick={() => save('Reply limits saved.', () => mk.saveSetting('reply_caps', { per_day: capDay, per_author_per_day: capAuthor }))}>Save reply limits</button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </MkPage>
  );
}

/** A boxed card whose body gets a `save(label, fn)` that shows the result inside the card. */
function Card({ span, title, sub, children }: {
  span: number;
  title: string;
  sub?: string;
  children: (save: (label: string | null, fn: () => Promise<unknown>) => void) => ReactNode;
}) {
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const save = (label: string | null, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true); setFlash(null);
    fn().then((r) => setFlash({ tone: 'good', text: label ?? String(r) }))
      .catch((e) => setFlash({ tone: 'warn', text: errorText(e) }))
      .finally(() => setBusy(false));
  };
  return (
    <section className={`bk-card bk-span-${span}`} aria-busy={busy}>
      <div className="bk-card-head"><h2 className="bk-card-title">{title}</h2>{sub && <span className="bk-card-sub">{sub}</span>}</div>
      <div className="bk-card-body">
        {children(save)}
        {busy && <p className="bk-stat-sub">Working…</p>}
        {flash && <Note tone={flash.tone}>{flash.text}</Note>}
      </div>
    </section>
  );
}

function statusWords(s: string | undefined): string {
  return s === 'ok' ? 'Connected' : s === 'blocked' ? 'Blocked by Meta' : s === 'error' ? 'Check failed' : s === 'unconfigured' || !s ? 'Unconfigured' : s;
}
function evenWeights(): Record<Pillar, number> {
  const w = {} as Record<Pillar, number>;
  const each = round2(1 / PILLARS.length);
  PILLARS.forEach((p) => { w[p] = each; });
  w[PILLARS[0]] = round2(1 - each * (PILLARS.length - 1));
  return w;
}
function scaleToOne(w: Record<Pillar, number>): Record<Pillar, number> {
  const total = PILLARS.reduce((a, p) => a + w[p], 0) || 1;
  const out = {} as Record<Pillar, number>;
  PILLARS.forEach((p) => { out[p] = round2(w[p] / total); });
  // Put the rounding remainder on the largest pillar so the total is exactly 1.
  const drift = round2(1 - PILLARS.reduce((a, p) => a + out[p], 0));
  const top = PILLARS.reduce((a, p) => (out[p] > out[a] ? p : a), PILLARS[0]);
  out[top] = round2(out[top] + drift);
  return out;
}
function normalize(w: Record<Pillar, number>): Record<Pillar, number> {
  return scaleToOne(w);
}
